<template>
  <div class="payment-progress">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        :show-close-button="false"
        class="payment-progress !w-[700px] !max-w-[700px]"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ $t("payment.follow-instructions") }}
          </SDialogTitle>
        </SDialogHeader>
        <SButton
          v-if="showManualPaymentOption"
          type="button"
          variant="outline"
          size="icon"
          class="close-button rounded-full"
          aria-label="Close"
          @click="close"
        >
          <SvgIcon
            :iconContent="closeIcon"
            iconAlt="close"
            iconStyle="width: 20px; height: auto;"
            class="close-icon"
          />
        </SButton>
        <div class="flex flex-col items-center justify-center">
          <p class="text-[22px] font-bold">
            {{ $t("payment.follow-instructions") }}
          </p>
          <div class="mt-10 mb-16">
            <SvgIcon :iconContent="iconContent" :iconStyle="iconStyle" />
          </div>
          <div
            v-if="showManualPaymentOption"
            class="mb-5 flex items-center gap-1"
          >
            <span>{{ $t("payment.already-completed") }}</span>
            <SButton
              type="button"
              variant="link"
              class="px-1 font-bold underline"
              @click="toggleManualPaymentModal(true)"
            >
              {{ $t("payment.complete-manually") }}
            </SButton>
          </div>
          <div class="mb-5" v-else>
            {{ $t("payment.wait-for-completion") }} ... ({{ timeLeft }}s)
          </div>
          <div class="mb-5 flex items-center gap-1">
            <span>{{ $t("payment.want-to-cancel") }}</span>
            <SButton
              type="button"
              variant="link"
              class="px-1 font-bold underline text-destructive"
              @click="abortPayment"
            >
              {{ $t("payment.cancel") }}
            </SButton>
          </div>
        </div>
      </SDialogContent>
    </SDialog>

    <SDialog v-model:open="showManualPaymentModal">
      <SDialogContent class="manual-payment-modal !w-[460px] !max-w-[460px]">
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ t("payment.complere-reference") }}
          </SDialogTitle>
        </SDialogHeader>
        <div class="flex flex-col items-center gap-6 px-4 pt-2 pb-2">
          <p
            class="max-w-sm text-center text-[15px] leading-relaxed text-foreground"
          >
            {{ t("payment.complere-reference") }}
          </p>
          <SInputGroup class="h-12 w-fit">
            <SInputGroupAddon align="inline-start">
              <SInputGroupText class="font-mono text-sm font-semibold">
                {{ referencePrefix }}
              </SInputGroupText>
            </SInputGroupAddon>
            <SInputGroupInput
              class="h-full w-40 font-mono text-base tracking-wide"
              :model-value="userInput"
              @update:model-value="(v) => (userInput = String(v))"
              placeholder="XXXXXXXXXX"
              :disabled="noReceipt"
            />
          </SInputGroup>
          <SButton
            type="button"
            variant="link"
            class="h-auto p-0 text-[13px] font-normal text-muted-foreground underline"
            @click="showRecieptExampleModal = true"
          >
            {{ $t("payment.highlight-reference") }}
          </SButton>
          <label class="flex cursor-pointer items-center gap-2 text-sm">
            <SCheckbox
              :model-value="noReceipt"
              @update:model-value="(v) => (noReceipt = Boolean(v))"
            />
            <span>{{ $t("payment.no-receipt") }}</span>
          </label>
          <SButton
            type="button"
            size="lg"
            class="mt-1 min-w-[180px]"
            :disabled="!noReceipt && !isMatchingReference"
            @click="completePaymentManually"
          >
            {{ $t("button.confirm") }}
          </SButton>
        </div>
      </SDialogContent>
    </SDialog>

    <SDialog v-model:open="showRecieptExampleModal">
      <SDialogContent
        class="receipt-example-modal !w-[420px] !max-w-[420px]"
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ $t("payment.highlight-reference") }}
          </SDialogTitle>
        </SDialogHeader>
        <div class="receipt-example">
          <div class="receipt-example-title">CARDHOLDER COPY</div>

          <div class="receipt-example-rows">
            <div class="receipt-example-row">
              <span class="label">Date</span
              ><span class="value">27.12.2023</span>
            </div>
            <div class="receipt-example-row">
              <span class="label">Time</span><span class="value">12:00:00</span>
            </div>
            <div class="receipt-example-row">
              <span class="label">Card</span><span class="value">********</span>
            </div>
            <div class="receipt-example-row">
              <span class="label">PAN seq.</span><span class="value">**</span>
            </div>
            <div class="receipt-example-row">
              <span class="label">Pref.name</span
              ><span class="value">********</span>
            </div>
            <div class="receipt-example-row">
              <span class="label">Card type</span><span class="value">***</span>
            </div>
            <div class="receipt-example-row">
              <span class="label">Payment method</span
              ><span class="value">********</span>
            </div>
            <div class="receipt-example-row">
              <span class="label">Payment variant</span
              ><span class="value">***</span>
            </div>
            <div class="receipt-example-row receipt-example-row--last-in-group">
              <span class="label">Entry mode</span
              ><span class="value">********</span>
            </div>

            <div class="receipt-example-row">
              <span class="label">AID</span
              ><span class="value">****************</span>
            </div>
            <div class="receipt-example-row">
              <span class="label">MID</span><span class="value">**</span>
            </div>
            <div class="receipt-example-row">
              <span class="label">TID</span
              ><span class="value">****************</span>
            </div>
            <div class="receipt-example-row receipt-example-row--last-in-group">
              <span class="label">PTID</span><span class="value">********</span>
            </div>

            <div class="receipt-example-row">
              <span class="label">Auth. code</span
              ><span class="value">******</span>
            </div>
            <div class="receipt-example-row">
              <span class="label">Tender</span
              ><span class="value">****************</span>
            </div>
            <div class="receipt-example-row receipt-example-highlight">
              <span class="label">Reference</span>
              <span class="value">NEW_POS_*****_**********_**********</span>
            </div>

            <div class="receipt-example-row">
              <span class="label">Type</span
              ><span class="value">GOODS_SERVICES</span>
            </div>
            <div class="receipt-example-row">
              <span class="label">Total</span
              ><span class="value">EUR 100.00</span>
            </div>
          </div>
        </div>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { ModalComponents, ModalStatus } from "@/constants/modal";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import paymentCardsSvg from "@/assets/images/svg/payment-cards.svg";
import PaymentService, {
  PaymentServiceStatus,
} from "@/services/payment-service";
import { defineComponent } from "vue";
import BookingModule from "@/store/modules/booking";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { LogLevel, LogService } from "@/services/logging";
import { ref } from "vue";
import { computed } from "vue";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";
import { useI18n } from "vue-i18n";
import ModalModule from "@/store/modules/modal";
import closeIcon from "@/assets/images/svg/close.svg";
import AppModule from "@/store/modules/app";
import { AdyenResponseStatus } from "@/interfaces/store/IBooking";
import cloneDeep from "lodash";
import { paymentHandler } from "@/utils/vue-helpers/booking";
import i18n from "@/lang";
import {
  SButton,
  SCheckbox,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupInput,
  SInputGroupText,
} from "@smeetz/ui-core";

export default defineComponent({
  name: ModalComponents.PaymentProgress,
  components: {
    SButton,
    SCheckbox,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
    SInputGroup,
    SInputGroupAddon,
    SInputGroupInput,
    SInputGroupText,
  },
  setup() {
    const dialogVisible =
      true || dialogVisibleForStatus(ModalStatus.PaymentProgress);
    const iconContent = paymentCardsSvg;
    const iconStyle = "width: 216px; height: 212px";
    const { t } = useI18n({});

    const showManualPaymentOption = ref(false);
    const timeLeft = ref(10);
    const showManualPaymentModal = ref(false);
    const paymentReference = ref();
    const userInput = ref("");
    const noReceipt = ref(false);
    const showRecieptExampleModal = ref(false);

    const referenceParts = PaymentService.transactionId.split("_");
    const accountingUuid = referenceParts.pop();
    const referencePrefix = referenceParts.join("_") + "_";

    const isMatchingReference = computed(() => {
      return accountingUuid === userInput.value;
    });

    const cantCheckStatus = computed(() => {
      return PaymentService.status === PaymentServiceStatus.CantCheckStatus;
    });

    return {
      dialogVisible,
      iconContent,
      iconStyle,
      t,
      showManualPaymentOption,
      showManualPaymentModal,
      paymentReference,
      userInput,
      noReceipt,
      referencePrefix,
      accountingUuid,
      isMatchingReference,
      showRecieptExampleModal,
      cantCheckStatus,
      closeIcon,
      timeLeft,
    };
  },
  mounted() {
    // show manual completion button after 10s
    const timerInterval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
      } else {
        clearInterval(timerInterval);
        this.showManualPaymentOption = true;
      }
    }, 1000);
  },
  methods: {
    async abortPayment() {
      LogService.info(
        `User clicked to abort Adyen payment, booking: ${BookingModule.selectedBooking?.uuid}`
      );
      const selectedBooking = BookingModule.selectedBooking;
      if (!selectedBooking) {
        LogService.info(
          `${PSBErrors.NoBookingInModule} when trying to abort payment ${PaymentService.transactionId}`,
          undefined,
          LogLevel.Error
        );
        throw new Error(PSBErrors.NoBookingInModule);
      }
      await PaymentService.abortPayment(selectedBooking);
      // BookingModule.setAdyenResponseStatusTrack({
      //   bookingUuid: selectedBooking.uuid!,
      //   accountingUuid: PaymentService.serviceId,
      //   status: AdyenResponseStatus.Aborted,
      // });
      // // immediately hide the modal to not block the user in case of payment terminal issues
      // ModalModule.setStatus(ModalStatus.Hidden);
      // AppModule.updatedIsLoading(false);
    },
    async completePaymentManually() {
      LogService.info(
        `User clicked to manually complete Adyen payment: ${PaymentService.transactionId}`
      );
      const selectedBooking = BookingModule.selectedBooking;
      if (!selectedBooking) {
        LogService.info(
          `${PSBErrors.NoBookingInModule} when trying to manually complete payment ${PaymentService.transactionId}`,
          undefined,
          LogLevel.Error
        );
        throw new Error(PSBErrors.NoBookingInModule);
      }
      if (this.accountingUuid === this.userInput) {
        // the user entered a correct reference, let's proceed with completing the payment manually
        PaymentService.completePaymentManually(selectedBooking);
      } else if (this.noReceipt) {
        // Translation copy still ships `<br>` tags (legacy from the old
        // ElMessageBox.dangerouslyUseHTMLString path). Normalize to `\n` so
        // ConfirmDialog's `whitespace-pre-line` body renders the breaks.
        const warning = this.t("payment.manual-payment-warning").replace(
          /<br\s*\/?>/gi,
          "\n"
        );
        const confirmed = await showConfirm({
          title: this.t("common.warning"),
          message: warning,
          variant: "warning",
        });

        if (confirmed) {
          LogService.info(
            `User confirmed completing the payment manually without the payment reference, payment: ${PaymentService.transactionId}`
          );
          PaymentService.completePaymentManually(selectedBooking);
        } else {
          LogService.info(
            `User did not confirm completing the payment manually, payment: ${PaymentService.transactionId}`
          );
        }
      }
    },
    toggleManualPaymentModal(value: boolean) {
      this.showManualPaymentModal = value;
    },
    async close() {
      LogService.info(
        "Prompting user to confirm closing payment progress modal"
      );
      const confirmClosing = await showConfirm({
        title: this.t("payment-modal.confirm-closing-title"),
        message: this.t("payment-modal.confirm-closing-message"),
        variant: "warning",
        confirmText: this.t("payment-modal.confirm"),
        cancelText: this.t("button.cancel"),
      });
      if (!confirmClosing) {
        LogService.info(`User did not confirm closing payment progress modal`);
        return;
      }
      const selectedBooking = BookingModule.selectedBooking;
      if (!selectedBooking || !selectedBooking.uuid) {
        LogService.info(
          `${PSBErrors.NoBookingInModule} when trying to close adyen payment modal ${PaymentService.transactionId}`,
          null,
          LogLevel.Error
        );
        throw new Error(PSBErrors.NoBookingInModule);
      }
      LogService.info(
        `User clicked to close the Adyen payment modal, booking: ${selectedBooking.uuid}`
      );
      const selectedBookingAdyenReponseStatusTrack =
        BookingModule.selectedBookingAdyenReponseStatusTrack;
      if (
        selectedBookingAdyenReponseStatusTrack[PaymentService.serviceId] ===
        AdyenResponseStatus.Initiated
      ) {
        // if the user closes the modal because they had no response from the terminal and the payment wasn't completed, we report it as unresponsive
        // and we remove the accounting
        LogService.info(
          "Current service ID status was initiated but never completed, marking the payment as failed",
          { serviceID: PaymentService.serviceId }
        );
        BookingModule.setAdyenResponseStatusTrack({
          bookingUuid: selectedBooking.uuid,
          accountingUuid: PaymentService.serviceId,
          status: AdyenResponseStatus.Unresponsive,
        });
        paymentHandler(PaymentServiceStatus.Failed);
      } else if (
        [
          AdyenResponseStatus.InitialPaymentFailed,
          AdyenResponseStatus.Unresponsive,
        ].includes(
          selectedBookingAdyenReponseStatusTrack[PaymentService.serviceId]
        )
      ) {
        // if we already received an error or the payment api call was unresponsive, plus the modal still hasn't been closed because paymentStatus also faced problems
        // then we treat the payment as if it failed, and inform the user.
        // this also prevents keeping pending accountings in the booking
        LogService.info(
          "Current service ID status is either unresponsive or failed, marking the payment as failed",
          { serviceID: PaymentService.serviceId }
        );
        paymentHandler(PaymentServiceStatus.Failed);
      }
      PaymentService.resetPaymentService();
      ModalModule.setStatus(ModalStatus.Hidden);
      AppModule.updatedIsLoading(false);
    },
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
.payment-progress {
  .close-button {
    position: absolute;
    right: 20px;
    top: 20px;
    height: 40px;
    width: 40px;
    padding: 0;
  }
}
.receipt-example-modal {
  .receipt-example {
    display: flex;
    flex-direction: column;
    padding: 4px 8px 8px;
    font-family: "SFMono-Regular", Menlo, Consolas, "Liberation Mono", monospace;
    font-size: 11px;
    line-height: 1.4;
    color: #111;
  }

  .receipt-example-title {
    text-align: center;
    font-weight: 700;
    letter-spacing: 0.04em;
    margin-bottom: 14px;
    font-size: 12px;
  }

  .receipt-example-rows {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .receipt-example-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;

    .label {
      flex: 0 0 auto;
      color: #444;
    }

    .value {
      flex: 1 1 auto;
      text-align: right;
      word-break: break-all;
      font-weight: 500;
    }
  }

  .receipt-example-row--last-in-group {
    margin-bottom: 8px;
  }

  .receipt-example-highlight {
    margin: 6px 0;
    padding: 8px 10px;
    border: 2px solid #ef4444;
    border-radius: 4px;
    background-color: #fef2f2;

    .value {
      color: #b91c1c;
      font-weight: 700;
    }
  }
}
</style>
